import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { format } from 'date-fns';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DraftClaim } from '@amfam/claim/fnol/data-access';
import { ClaimGlassOnly, fromClaimGlassOnlyActions } from '@amfam/claim/glass-only/data-access';
import { env } from '@amfam/environment';
import { PolicySelectors } from '@amfam/policy/data-access';
import { FnolRiskModel, GenericProductType, RiskCompare } from '@amfam/policy/models';
import { AnalyticsFacade, ClaimsAnalytics } from '@amfam/shared/analytics';
import { ClaimsNavigationPaths, MatDatePickerOptions } from '@amfam/shared/models';
import { QuickLink } from '@amfam/shared/ui/quick-links';
import { userQuery, UserState } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
// date-fns
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { DockingBarService, PageTitleService } from '@amfam/ui-kit';

import * as fromRoot from '../../core/store';

@Component({
  selector: 'ds-report-claim-glass-only',
  templateUrl: './report-claim-glass-only.component.html',
  styleUrls: ['./report-claim-glass-only.component.scss'],
  standalone: false
})
export class ReportClaimGlassOnlyComponent implements OnInit, OnDestroy {
  isContactBillingToggleEnabled = this.featureFlagService.isEnabled('contact_billing');

  public quickLinksList = new Array<QuickLink>();
  public riskSelectionForm: UntypedFormGroup;
  public dateGroup: UntypedFormGroup;
  public dateOfIncident: UntypedFormControl;
  public dataLoading: Observable<boolean>;
  public risks: Array<FnolRiskModel> = new Array<FnolRiskModel>();
  private currentRisk: FnolRiskModel;
  private riskSelection: FnolRiskModel;
  public policy: DraftClaim;
  private currentUser: UserState;
  private phoneNumber: string;

  matDatePickerOptions: MatDatePickerOptions;

  // Shared Subject for completing all observables on destroy
  private stop$: Subject<void> = new Subject<void>();

  contactNumber: string = env.numberLink;

  constructor(
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private store: Store<fromRoot.RootState>,
    private formBuilder: UntypedFormBuilder,
    private analyticsFacade: AnalyticsFacade,
    public roleService: ImpersonateRolesService,
    private featureFlagService: FeatureFlagService
  ) {
    this.store
      .select(BrandSelectors.selectIsPartner)
      .pipe(takeUntil(this.stop$))
      .subscribe(isPartner => {
        this.quickLinksList = [
          new QuickLink('Make a Payment', '/billing'),
          new QuickLink('Request a Change', '/policies/changerequest'),
          new QuickLink('Contact a Billing Specialist', '/billing/contact'),
          new QuickLink('FAQs', env.links.amfamDotComClaimsFaqUrl, 'amfam')
        ]
          .filter(link => !(_get(link, 'text') === 'FAQs' && isPartner))
          .filter(
            link =>
              // fitler Contact a Billing Specialist link if contact billing is toggled off
              !(
                _get(link, 'text') === 'Contact a Billing Specialist' &&
                !this.isContactBillingToggleEnabled
              )
          );
      });
  }

  ngOnInit() {
    // Async Pipe aggregate loading information
    this.dataLoading = this.store.select(PolicySelectors.selectPoliciesLoading);

    this.buildForm();

    combineLatest(
      this.store.select(PolicySelectors.selectClaimableRiskModels),
      this.store.select(userQuery.selectUserState),
      this.store.select(userQuery.selectPartyDetails),
      (risks, user, partyData) => ({
        risks: risks,
        user: user,
        partyData: partyData
      })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        this.risks = state.risks
          .sort(RiskCompare.compare)
          .filter(risk => risk.generalizedProductType === GenericProductType.Auto);

        if (state.user) {
          this.currentUser = state.user;
        }

        if (state.partyData && state.partyData.primaryPhone) {
          this.phoneNumber = state.partyData.primaryPhone.tenDigitPhoneNumber;
        }
      });

    this.matDatePickerOptions = {
      maxDate: new Date()
    };

    this.pageTitle.set('Report a Glass-Only Claim');
    this.dockingBarService.registerHeading('Report a Glass-Only Claim');
    this.analyticsFacade.trackPage(ClaimsAnalytics.pageGlassClaim);
  }

  buildForm() {
    const d: Date = new Date();
    this.riskSelectionForm = this.formBuilder.group({
      dateGroup: this.formBuilder.group({
        dateOfIncident: [
          {
            date: {
              year: d.getFullYear(),
              month: d.getMonth() + 1,
              day: d.getDate()
            }
          },
          Validators.compose([Validators.required])
        ]
      })
    });
    this.dateGroup = <UntypedFormGroup>this.riskSelectionForm.controls['dateGroup'];
  }

  changeRisk(riskId) {
    this.currentRisk = this.risks.find(risk => risk.id === riskId);
  }

  cancel() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [ClaimsNavigationPaths.CLAIM_HOME_PATH]
      })
    );
  }

  openContactUs() {
    window.open(this.contactNumber, '_blank');
  }

  navigateToGlassClaim() {
    let successUrl = window.location.protocol + '//' + window.location.hostname;
    if (window.location.port) {
      successUrl += ':' + window.location.port;
    }
    successUrl += '/claims';

    const safelitePayload: ClaimGlassOnly = {
      policyNumber: this.currentRisk.policyNumber,
      lossDate: format(this.dateGroup.controls['dateOfIncident'].value, 'MM/DD/YYYY'),
      returnURL: successUrl,
      returnURL2: successUrl, // AS: Safelite will handle the error on their screen, they will not redirect.
      firstName: this.currentUser.firstName,
      lastName: this.currentUser.lastName,
      address1: this.currentRisk.address.addressLine1,
      address2: this.currentRisk.address.addressLine2,
      city: this.currentRisk.address.city,
      state: this.currentRisk.address.state,
      zip: this.currentRisk.address.zipCode,
      email: this.currentUser.emailAddress,
      phoneNumber: this.phoneNumber
    };
    this.store.dispatch(new fromClaimGlassOnlyActions.LoadGlassOnlyClaims(safelitePayload));
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
